// Header.jsx
import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { NavLink, Link, useNavigate, useLocation, useParams } from "react-router-dom";
import { CombinedContext } from "../../context/combinedContext";
import "./navbar.css";
import generalRequest from "../../request/generalRequest";

import { Home, Book, Calendar, User, Store, Briefcase, Search, MapPin, StoreIcon, CreditCard, UserCircle, Contact, IdCard, MessagesSquare, ChartColumn } from "lucide-react";

import ReactCountryFlag from "react-country-flag";
import translations from "../../translations/translations";
import logo from "../../images/logoTransparent.png";
import OnboardingProgressBarAdmin from "./onboardingProgressBar/onboardingProgressBarAdmin";
import OnboardingProgressBarStaff from "./onboardingProgressBar/onboardingProgressBarStaff";
import CATEGORY_OPTIONS_SEARCH from "../../constants/categoryOptionsSearch";
import BannerMessageFixed from "../common/bannerMessageFixed/bannerMessageFixed";
import BannerMessage from "../common/bannerMessage/bannerMessage";

// Pages that should show search + categories
const DISCOVERY_PREFIXES = ["/", ...CATEGORY_OPTIONS_SEARCH.map((c) => `/${c.value}`)];

// Checkout is the tail end of onboarding itself: every step is already done by the
// time the page opens, and its launch button only re-runs billing setup and lands
// back here — so the whole bar is noise beside a payment overlay.
const NO_ONBOARDING_BAR_PATHS = ["/checkout"];

const Header = () => {
	const { userData, language, setLanguage, category, setCategory, setShopsData } = useContext(CombinedContext);

	const [search, setSearch] = useState("");
	const [locationInput, setLocationInput] = useState("");

	const navigate = useNavigate();
	const role = userData?.user?.role;
	const isLoggedIn = !!userData?.user;

	const location = useLocation();

	const { pathname } = useLocation();

	const mainCategory = CATEGORY_OPTIONS_SEARCH.find((c) => pathname.startsWith(`/${c.value}`))?.value;
	const subCategory = (() => {
		const match = CATEGORY_OPTIONS_SEARCH.find((c) => pathname.startsWith(`/${c.value}`));
		if (!match) return undefined;
		const sub = pathname.replace(`/${match.value}/`, "").split("/")[0];
		return sub && sub !== match.value ? sub : undefined;
	})();

	const activeMainCategory = CATEGORY_OPTIONS_SEARCH.find((c) => c.value === mainCategory);

	const [banner, setBanner] = useState(null);
	const [exiting, setExiting] = useState(false);

	// The bar is sticky: it condenses once the page scrolls, and publishes its own
	// height as --sf-nav-h so pages with their own sticky rows can offset below it.
	const headerRef = useRef(null);
	const sentinelRef = useRef(null);
	const [isScrolled, setIsScrolled] = useState(false);

	// A sentinel above the bar rather than a scroll handler: the observer is driven
	// off the compositor, so there is no per-frame work and no layout thrash.
	useEffect(() => {
		const el = sentinelRef.current;
		if (!el || typeof IntersectionObserver === "undefined") return;

		const io = new IntersectionObserver(([entry]) => setIsScrolled(!entry.isIntersecting), { threshold: 0 });
		io.observe(el);
		return () => io.disconnect();
	}, []);

	useLayoutEffect(() => {
		const el = headerRef.current;
		if (!el) return;

		const publishHeight = () => {
			// Only the expanded height is published. It is the stable maximum, so the
			// sticky rows on the pages below stay clear of the bar while it condenses
			// rather than being chased up and down by the transition.
			if (el.classList.contains("is-scrolled")) return;
			document.documentElement.style.setProperty("--sf-nav-h", `${Math.round(el.getBoundingClientRect().height)}px`);
		};

		publishHeight();
		if (typeof ResizeObserver === "undefined") return;

		const ro = new ResizeObserver(publishHeight);
		ro.observe(el);
		return () => ro.disconnect();
	}, []);

	const showBanner = (type, text, duration = 7000) => {
		setBanner({ type, text });
		setExiting(false);

		setTimeout(() => {
			setExiting(true);
		}, duration - 250);

		setTimeout(() => {
			setBanner(null);
			setExiting(false);
		}, duration);
	};

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		if (params.get("showLiveBanner")) {
			showBanner("success", translations[language].checkoutPage.shopLive || "Your shop is now live 🚀");
		}
	}, []);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		if (params.get("showLiveBannerStaff")) {
			showBanner("success", translations[language].onboarding.staff.messages.success || "Your shop is now live 🚀");
		}
	}, []);

	const hideOnboardingBar = NO_ONBOARDING_BAR_PATHS.includes(pathname);

	// Check if current page starts with any discovery prefix
	const isDiscoveryPage = DISCOVERY_PREFIXES.some((p) => pathname.startsWith(p));

	// Search on Enter only
	const handleSearchEnter = (e) => {
		if (e.key === "Enter") triggerSearch();
	};

	const triggerSearch = () => {
		const params = new URLSearchParams();
		if (search.trim()) params.set("q", search.trim());
		if (locationInput.trim()) params.set("loc", locationInput.trim());
		navigate(`/barbershop/barbershop?${params.toString()}`);
	};

	// const triggerSearch = async () => {
	//     try {
	//         const data = await generalRequest.searchShops(
	//             search.trim() || "",
	//             1,
	//             20,
	//             locationInput.trim() || undefined
	//         );
	//         setShopsData(data.shops);
	//     } catch (err) {
	//         console.error(err);
	//     }
	// };

	const handleMainCategoryClick = (cat) => {
		if (cat.subCategories?.length > 0) {
			const firstSub = cat.subCategories[0];
			navigate(`/${cat.value}/${firstSub.value}`);
			setCategory(firstSub.value);
			return;
		}
		navigate(`/${cat.value}`);
		setCategory(cat.value);
	};

	const handleSubCategoryClick = (sub) => {
		navigate(`/${activeMainCategory.value}/${sub.value}`);
		setCategory(sub.value);
	};

	// ==========================
	// RENDER COMPONENT
	// ==========================
	// Fragment, not a wrapper div: a sticky header can only travel inside its
	// parent's box, so it has to sit directly under .app-container. Banner is fixed.
	return (
		<>
			<div ref={sentinelRef} className="sf-nav-sentinel" aria-hidden="true" />
			<header ref={headerRef} className={`sf-nav ${isScrolled ? "is-scrolled" : ""}`}>
				<div className="sf-nav-without-progress-bar">
					<div className={`sf-nav-inner ${role === "admin" || role === "owner" || role === "staff" ? "sf-nav-inner--compact" : ""}`}>
						{/* LOGO */}
						<Link to="/" className="sf-nav-brand">
							<img src={logo} alt="ReservaSinFila" className="sf-header-logo" />
						</Link>

						{/* ==========================
                    ADMIN / OWNER LINKS
                ========================== */}
						{(role === "admin" || role === "owner") && (
							<div className="sf-nav-admin-links">
								<NavLink to="/calendar" className={({ isActive }) => `sf-cat ${isActive ? "is-active" : ""}`}>
									{translations[language].navbar.admin.calendar}
								</NavLink>

								<NavLink to="/profile" className={({ isActive }) => `sf-cat ${isActive ? "is-active" : ""}`}>
									{translations[language].navbar.auth.shop}
								</NavLink>

								<NavLink to="/team" className={({ isActive }) => `sf-cat ${isActive ? "is-active" : ""}`}>
									{translations[language].navbar.admin.team}
								</NavLink>

								<NavLink to="/reports" className={({ isActive }) => `sf-cat ${isActive ? "is-active" : ""}`}>
									{translations[language].navbar.admin.reports}
								</NavLink>
								{/* <NavLink to="/account/billing" className={({ isActive }) => `sf-cat ${isActive ? "is-active" : ""}`}>
									{translations[language].navbar.admin.billing}
								</NavLink> */}
							</div>
						)}

						{/* ==========================
                    STAFF LINKS
                ========================== */}
						{role === "staff" && (
							<div className="sf-nav-admin-links">
								<NavLink to="/calendar" className={({ isActive }) => `sf-cat ${isActive ? "is-active" : ""}`}>
									{translations[language].navbar.admin.calendar}
								</NavLink>

								<NavLink to="/staff/profile" className={({ isActive }) => `sf-cat ${isActive ? "is-active" : ""}`}>
									{translations[language].navbar.admin.profile}
								</NavLink>

								<NavLink to="/reports" className={({ isActive }) => `sf-cat ${isActive ? "is-active" : ""}`}>
									{translations[language].navbar.admin.reports}
								</NavLink>
							</div>
						)}

						{/* ==========================
                    CUSTOMER SEARCH BAR
                    (ONLY on discovery pages)
                ========================== */}
						{isDiscoveryPage && (!role || role === "customer") && (
							<div className="sf-nav-searchbar">
								{/* One capsule, two segments, one submit — reads as a single control */}
								<div className="sf-nav-searchbar-search">
									<div className="sf-nav-chip">
										<Search size={16} className="sf-icon" />

										<input
											className="sf-nav-input"
											placeholder={translations[language].navbar.search.placeholder}
											aria-label={translations[language].navbar.search.placeholder}
											value={search}
											onChange={(e) => setSearch(e.target.value)}
											onKeyDown={handleSearchEnter}
										/>
									</div>

									<span className="sf-nav-chip-divider" aria-hidden="true" />

									<div className="sf-nav-chip">
										<MapPin size={16} className="sf-icon" />

										<input
											className="sf-nav-input"
											placeholder={translations[language].navbar.search.where}
											aria-label={translations[language].navbar.search.where}
											value={locationInput}
											onChange={(e) => setLocationInput(e.target.value)}
											onKeyDown={handleSearchEnter}
										/>
									</div>

									<button
										type="button"
										className="sf-nav-search-submit"
										onClick={triggerSearch}
										aria-label={translations[language].navbar.search.button}
									>
										<Search size={17} />
										<span>{translations[language].navbar.search.button}</span>
									</button>
								</div>
							</div>
						)}

						{/* ==========================
                    LANGUAGE + PROFILE
                ========================== */}
						<div className="sf-nav-actions">
							{/* Language Toggle */}

							<button type="button" className="sf-nav-lang-display" onClick={() => setLanguage(language === "en" ? "es" : "en")}>
								<ReactCountryFlag countryCode={language === "en" ? "DO" : "CA"} svg style={{ width: 20, height: 14 }} />
								<span>{language === "en" ? "Español" : "English"}</span>
							</button>

							<span className="sf-nav-actions-divider" aria-hidden="true" />

							{/* Show ONLY when NOT logged in */}
							{!isLoggedIn && (
								<button className="sf-nav-signin" onClick={() => navigate("/businesslanding")}>
									<Store size={18} className="sf-nav-signin-icon" />
									<span>{translations[language].navbar.mobile.business}</span>
								</button>
							)}

							<Link to="/contact" className="sf-nav-signin">
								<MessagesSquare size={18} className="sf-nav-signin-icon" />
								<span>{translations[language].footer.links.contact}</span>
							</Link>

							{/* Sign In / Profile — the one solid button in the bar */}
							{!isLoggedIn ? (
								<Link to="/signin" className="sf-nav-signin sf-nav-signin--primary">
									<User size={18} className="sf-nav-signin-icon" />
									<span>{translations[language].navbar.auth.signin}</span>
								</Link>
							) : (
								<button className="sf-nav-signin sf-nav-signin--primary" onClick={() => navigate("/account")}>
									<User size={18} className="sf-nav-signin-icon" />
									<span>{translations[language].navbar.admin.account}</span>
								</button>
							)}
						</div>
					</div>

					<div className="sf-nav-categories-outer">
						{isDiscoveryPage && (!role || role === "customer") && (
							<>
								{/* MAIN CATEGORIES */}
								<nav className="sf-nav-rail" aria-label="Categories">
									<div className="sf-nav-categories">
										{CATEGORY_OPTIONS_SEARCH.filter((c) => !c.isFuture).map((cat) => {
											const isActiveMain = mainCategory === cat.value;

											return (
												<button
													key={cat.value}
													className={`sf-cat ${isActiveMain ? "is-active" : ""}`}
													aria-current={isActiveMain ? "page" : undefined}
													onClick={() => handleMainCategoryClick(cat)}
												>
													{language === "en" ? cat.labelEN : cat.labelES}
												</button>
											);
										})}
									</div>
								</nav>

								{/* SUB CATEGORIES */}
								{activeMainCategory && activeMainCategory.subCategories?.length > 1 && (
									<div className="sf-nav-rail">
										<div className="sf-nav-subcategories">
											{activeMainCategory.subCategories.map((sub) => (
												<button
													key={sub.value}
													className={`sf-subcat${subCategory === sub.value ? " is-active" : ""}`}
													aria-current={subCategory === sub.value ? "page" : undefined}
													onClick={() => handleSubCategoryClick(sub)}
												>
													{language === "en" ? sub.labelEN : sub.labelES}
												</button>
											))}
										</div>
									</div>
								)}
							</>
						)}
					</div>
				</div>

				{/* ONBOARDING BARS */}
				{!hideOnboardingBar && (role === "admin" || role === "owner") && <OnboardingProgressBarAdmin />}
				{!hideOnboardingBar && role === "staff" && <OnboardingProgressBarStaff />}
			</header>

			{/* Deliberately outside <header>: the condensed bar gets a backdrop-filter,
			    and a filtered ancestor becomes the containing block for fixed children,
			    which would peel this off the viewport bottom on every scroll. */}
			<div className="sf-nav-mobile-bottom">
				{/* CUSTOMER */}
				{(!role || role === "customer") && (
					<>
						<NavLink to="/barbershop/barbershop" className={({ isActive }) => `sf-nav-mobile-icon ${isActive ? "active" : ""}`}>
							<Home size={22} />
							<span>{translations[language].navbar.mobile.explore}</span>
						</NavLink>

						{isLoggedIn && (
							<NavLink to="/account/bookings" className={({ isActive }) => `sf-nav-mobile-icon ${isActive ? "active" : ""}`}>
								<Book size={22} />
								<span>{translations[language].navbar.mobile.bookings}</span>
							</NavLink>
						)}

						<NavLink to="/businesslanding" className={({ isActive }) => `sf-nav-mobile-icon ${isActive ? "active" : ""}`}>
							<Store size={22} />
							<span>{translations[language].navbar.mobile.business}</span>
						</NavLink>

						{isLoggedIn ? (
							<button className="sf-nav-mobile-icon" onClick={() => navigate("/account")}>
								<User size={22} />
								<span>{translations[language].navbar.admin.account}</span>
							</button>
						) : (
							<NavLink to="/signin" className={({ isActive }) => `sf-nav-mobile-icon ${isActive ? "active" : ""}`}>
								<User size={22} />
								<span>{translations[language].navbar.auth.signin}</span>
							</NavLink>
						)}
					</>
				)}

				{/* ADMIN / OWNER */}
				{(role === "admin" || role === "owner") && (
					<>
						<NavLink to="/calendar" className={({ isActive }) => `sf-nav-mobile-icon ${isActive ? "active" : ""}`}>
							<Calendar size={22} />
							<span>{translations[language].navbar.admin.calendar}</span>
						</NavLink>

						<NavLink to="/profile" className={({ isActive }) => `sf-nav-mobile-icon ${isActive ? "active" : ""}`}>
							<StoreIcon size={22} />
							<span>{translations[language].navbar.auth.shop}</span>
						</NavLink>

						<NavLink to="/team" className={({ isActive }) => `sf-nav-mobile-icon ${isActive ? "active" : ""}`}>
							<Briefcase size={22} />
							<span>{translations[language].navbar.admin.team}</span>
						</NavLink>

						<NavLink to="/reports" className={({ isActive }) => `sf-nav-mobile-icon ${isActive ? "active" : ""}`}>
							<ChartColumn size={22} />
							<span>{translations[language].navbar.admin.reports}</span>
						</NavLink>

						{/* <NavLink
							to="/account/billing"
							className={({ isActive }) => `sf-nav-mobile-icon ${isActive ? "active" : ""} footer-website-only`}
						>
							<CreditCard size={22} />
							{translations[language].navbar.admin.billing}
						</NavLink> */}

						<NavLink to="/account" className={({ isActive }) => `sf-nav-mobile-icon ${isActive ? "active" : ""}`}>
							<User size={22} />
							<span>{translations[language].navbar.admin.account}</span>
						</NavLink>
					</>
				)}

				{/* STAFF */}
				{role === "staff" && (
					<>
						<NavLink to="/calendar" className={({ isActive }) => `sf-nav-mobile-icon ${isActive ? "active" : ""}`}>
							<Calendar size={22} />
							<span>{translations[language].navbar.admin.calendar}</span>
						</NavLink>

						<NavLink to="/staff/profile" className={({ isActive }) => `sf-nav-mobile-icon ${isActive ? "active" : ""}`}>
							<IdCard size={22} />
							<span>{translations[language].navbar.admin.profile}</span>
						</NavLink>

						<NavLink to="/reports" className={({ isActive }) => `sf-nav-mobile-icon ${isActive ? "active" : ""}`}>
							<ChartColumn size={22} />
							<span>{translations[language].navbar.admin.reports}</span>
						</NavLink>

						<button className="sf-nav-mobile-icon" onClick={() => navigate("/account")}>
							<User size={22} />
							<span>{translations[language].navbar.admin.account}</span>
						</button>
					</>
				)}
			</div>

			{banner && <BannerMessage type={banner.type} text={banner.text} exiting={exiting} />}
		</>
	);
};

export default Header;
